import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/common/Icon';
import { useClickOutside } from 'hooks/useClickOutside';
import {
  fetchNotifications,
  fetchUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from 'features/notifications/notificationSlice';
import {
  selectNotifications,
  selectUnreadCount,
  selectNotificationsLoading,
} from 'features/notifications/notificationSelectors';
import './NotificationBell.css';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

const NotificationBell = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  const notifications = useSelector(selectNotifications);
  const unreadCount = useSelector(selectUnreadCount);
  const loading = useSelector(selectNotificationsLoading);

  // Fetch unread badge count on mount and every 5 minutes.
  useEffect(() => {
    dispatch(fetchUnreadCount());
    const id = setInterval(() => dispatch(fetchUnreadCount()), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [dispatch]);

  useClickOutside(wrapperRef, () => setIsOpen(false), isOpen);

  const handleBellClick = () => {
    if (!isOpen) {
      dispatch(fetchNotifications());
    }
    setIsOpen((v) => !v);
  };

  const handleNotificationClick = async (n) => {
    if (!n.isRead) {
      await dispatch(markNotificationRead(n.id));
    }
    setIsOpen(false);
    const route = n.navigation?.route ?? '/stage1';
    const pendingDays = n.filters?.pending_days ?? n.pendingDays;
    const historyDays = n.historyDays;
    navigate(`${route}?notification=true&pending_days=${pendingDays}&history_days=${historyDays}`);
  };

  const handleMarkAllRead = () => {
    dispatch(markAllNotificationsRead());
  };

  return (
    <div className="kvo-notif-bell" ref={wrapperRef}>
      <button
        type="button"
        className="kvo-header__icon-btn"
        onClick={handleBellClick}
        aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : 'Notifications'}
      >
        <Icon name="bell" size={20} />
        {unreadCount > 0 && (
          <span className="kvo-notif-bell__badge" aria-hidden="true">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="kvo-notif-panel" role="dialog" aria-label="Notifications">
          <div className="kvo-notif-panel__header">
            <span className="kvo-notif-panel__title">Notifications</span>
            {unreadCount > 0 && (
              <button
                type="button"
                className="kvo-notif-panel__mark-all"
                onClick={handleMarkAllRead}
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="kvo-notif-panel__body">
            {loading ? (
              <div className="kvo-notif-panel__empty">
                <span>Loading…</span>
              </div>
            ) : notifications.length === 0 ? (
              <div className="kvo-notif-panel__empty">
                <Icon name="check" size={20} />
                <span>You&apos;re all caught up</span>
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  className={`kvo-notif-item${n.isRead ? ' kvo-notif-item--read' : ''}`}
                  onClick={() => handleNotificationClick(n)}
                >
                  <div className="kvo-notif-item__icon">
                    <Icon name="alert" size={16} />
                  </div>
                  <div className="kvo-notif-item__content">
                    <p className="kvo-notif-item__title">{n.title}</p>
                    <p className="kvo-notif-item__msg">{n.message}</p>
                    <p className="kvo-notif-item__meta">
                      Pending for {n.pendingDays}–{n.historyDays} days
                    </p>
                  </div>
                  {!n.isRead && <span className="kvo-notif-item__dot" aria-hidden="true" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
